import { computed, ref, Ref, shallowRef, ShallowRef, watch } from "vue";
import { acceptHMRUpdate, defineStore } from "pinia";
import { refDebounced } from "@vueuse/core";
import {
    difference as _difference,
    differenceBy as _differenceBy,
    remove as _remove,
    uniqBy as _uniqBy,
    debounce as _debounce,
} from "lodash";
import useOpenbetApi from "@/api/composables/use-openbet-api";
import { SearchCompetition } from "@/shared/model/search";
import { DetailedSearch, DetailedSearchCompetition } from "@/shared/model/detailed-search";
import { EventDrilldown } from "@/shared/model/event";
import { useLiveSportsStore } from "@/live/stores/live-sports";
import { DRILLDOWN_LEAGUE_LEVEL_NUMBER } from "@/shared/utils/constants";
import { useDrilldownNodesStore } from "@/shared/stores/use-drilldown-nodes-store";
import { DrilldownNode } from "@/shared/model/drilldown-node";
import { useRoute } from "vue-router";
import analytics from "../analytics";
import { PATHS } from "@/router/paths";

const QUERIES_MAX = 10;
const MIN_QUERY_LENGTH = 3;
const ADD_ADDITIONAL_COMPETITIONS = false; // Add additional competitions (leagues) from events drilldown node levelNumber === DRILLDOWN_LEAGUE_LEVEL_NUMBER

export const useSearchStore = defineStore(
    "Search",
    () => {
        // getEventsDrilldownDetail is used here instead of getEventsDrilldown because it does not return 'long-term events' (like overall league winner etc.)
        const { getDetailedSearch, getEventsDrilldownDetail } = useOpenbetApi();

        const route = useRoute();
        const drilldownNodesStore = useDrilldownNodesStore();

        const searchValue: Ref<string> = ref("");
        const searchValueDebounced = refDebounced(searchValue, 300);
        const searchFocused = ref<boolean>(false);
        const queries: Ref<string[]> = ref([]); // persisted
        const loading: Ref<boolean> = ref(false);
        const actualQuery: Ref<string> = ref("");
        const liveOnlyResults: Ref<boolean> = ref(false);
        const detailedSearch: ShallowRef<DetailedSearch> = shallowRef();
        const competitions: Ref<SearchCompetition[]> = ref([]);
        const events: Ref<EventDrilldown[]> = ref([]);
        const openedSearchPosition: Ref<string> = ref("");

        const isLiveRoute = computed(() => route.path.startsWith(PATHS.LIVE));
        const queryMinLength = computed(() => searchValue.value?.length >= MIN_QUERY_LENGTH);
        const resultEmpty = computed(
            () =>
                !detailedSearch.value ||
                !Object.keys(detailedSearch.value).some(
                    (key) =>
                        ({
                            ...detailedSearch.value,
                            events: detailedSearch.value.events?.filter((e) =>
                                isLiveRoute.value ? e.detail.detail.liveNow : true,
                            ),
                        })?.[key].length > 0,
                ),
        );

        const debouncedSendAnalyticsEvent = _debounce(() => {
            const searchObj = {
                searchResults: {
                    term: actualQuery.value,
                    resultCount: competitions.value.length + events.value.length,
                    searchPlacement: openedSearchPosition.value,
                },
            };
            analytics.sendSearch(searchObj);
        }, 2000);

        const searchQuery = async (query: string, liveOnly: boolean = false) => {
            if (!query) return;

            actualQuery.value = query;
            loading.value = true;
            liveOnlyResults.value = liveOnly;
            detailedSearch.value = await getDetailedSearch({ query });

            processDetailedSearch(detailedSearch.value).finally(() => {
                loading.value = false;
            });

            debouncedSendAnalyticsEvent();
        };

        const addQuery = (query: string) => {
            const newQueries = [...queries.value];
            // Put searched query at the first place, we need to remove it first if it is already stored
            if (newQueries.includes(query)) {
                _remove(newQueries, (n) => n === query);
            }
            // Keep only limited number of queries stored
            if (newQueries.length >= QUERIES_MAX) {
                newQueries.pop();
            }
            // Put query at the first place
            queries.value = [query].concat(newQueries);
        };

        const clearQueries = () => {
            searchValue.value = "";
            queries.value = [];
        };

        const clearResults = () => {
            detailedSearch.value = undefined;
            competitions.value = [];
            events.value = [];
        };

        const saveActualQuery = () => {
            addQuery(actualQuery.value);
        };

        const remapToSearchDetail = async (item: DetailedSearchCompetition | DrilldownNode) => {
            const drilldownNodes = await drilldownNodesStore.getDrilldownNodesById(item.id);
            return {
                displayOrder: item.displayOrder,
                id: item.id,
                name: item.name,
                parentDrilldownNodeId: item.parentDrilldownNodeId,
                drilldownNodes,
            } as SearchCompetition;
        };

        const processDetailedSearchEvents = async (search: DetailedSearch) => {
            const newEvents: EventDrilldown[] = [];
            const liveEvents = search.events.filter((e) => e.detail.detail.liveNow);
            const prematchEvents = !liveOnlyResults.value ? search.events.filter((e) => !e.detail.detail.liveNow) : [];
            const liveEventIds = liveEvents?.map((e) => e.detail.id);
            const prematchEventIds = prematchEvents?.map((e) => e.detail.id);

            if (liveEventIds?.length) {
                let liveEventDrilldowns: EventDrilldown[] = [];
                // Prevent initialization of LiveSportsStore on prematch page
                if (liveOnlyResults.value) {
                    const liveSportsStore = useLiveSportsStore();
                    // Will take a look into store first (it's much quicker) and if desired event won't be found (much likely) it'll call api
                    liveEventDrilldowns = liveSportsStore.eventDrilldownsByActiveTab.filter((e) =>
                        liveEventIds.includes(e.id),
                    );
                    newEvents.push(...liveEventDrilldowns);
                }
                const missingLiveEventIds = _difference(
                    liveEventIds,
                    liveEventDrilldowns.map((e) => e.id),
                );
                const missingLiveEventDrilldowns = !missingLiveEventIds?.length
                    ? []
                    : await getEventsDrilldownDetail(
                          {
                              eventIds: missingLiveEventIds.map((i) => Number(i)),
                              liveNow: true,
                          },
                          { wsData: { update: true } },
                      );
                newEvents.push(...missingLiveEventDrilldowns);
            }

            if (prematchEventIds?.length) {
                const prematchEventDrilldowns = await getEventsDrilldownDetail(
                    {
                        eventIds: prematchEventIds.map((i) => Number(i)),
                        liveNow: false,
                    },
                    { wsData: { update: true } },
                );
                newEvents.push(...prematchEventDrilldowns);
            }
            events.value = newEvents;
        };

        const processDetailedSearchCompetitions = async (search: DetailedSearch) => {
            const newCompetitions: SearchCompetition[] = [];
            if (search.competitions.length) {
                // We need to get drilldownNodes here, not just derivate DetailedSearchCompetition because we need to determine what kind of sport it is (because of icon) and if is Esport customize the category name
                const newDetailedCompetitions = search.competitions.map((c) => c.detail);
                let drilldowns = drilldownNodesStore.drilldownNodesFlat.filter((d) =>
                    newDetailedCompetitions.map((c) => c.id).includes(d.id),
                );
                // Prevent initialization of LiveSportsStore on prematch page
                if (liveOnlyResults.value) {
                    // We are unable to determine whether competition is live or not by given data from endpoint, so if competition has some live events let's declare it as live
                    const liveSportsStore = useLiveSportsStore();
                    const liveLeagueDrilldowns = _uniqBy(
                        liveSportsStore.eventDrilldownsByActiveTab.flatMap((e) =>
                            e.drilldownNodes.find((d) => d?.levelNumber === DRILLDOWN_LEAGUE_LEVEL_NUMBER),
                        ),
                        "id",
                    );
                    const liveLeagueDrilldownIds = liveLeagueDrilldowns.map((l) => l.id);
                    drilldowns = drilldowns.filter((d) => liveLeagueDrilldownIds.includes(d.id));
                }
                const remappedCompetitions = !drilldowns.length
                    ? []
                    : await newDetailedCompetitions.mapAsync(async (c) => await remapToSearchDetail(c));
                newCompetitions.push(...remappedCompetitions);
            }
            if (ADD_ADDITIONAL_COMPETITIONS && search.events.length) {
                const drilldownNodes = search.events
                    .filter((f) => (liveOnlyResults.value ? f.detail.detail.liveNow : true))
                    .flatMap((e) => e.detail.detail.drilldownNodes);
                const leagues = drilldownNodes?.filter((d) => d?.levelNumber === DRILLDOWN_LEAGUE_LEVEL_NUMBER);
                const drilldownCompetitions = await _uniqBy(leagues, "id").mapAsync(
                    async (d) => await remapToSearchDetail(d),
                );
                newCompetitions.push(..._differenceBy(drilldownCompetitions, newCompetitions, "id"));
            }
            competitions.value = newCompetitions;
        };

        const processDetailedSearch = async (search: DetailedSearch) => {
            if (!search) {
                clearResults();
                return;
            }
            // We are processing only events and competitions, sports is always empty array in the endpoint response.
            await processDetailedSearchEvents(search);
            await processDetailedSearchCompetitions(search);
        };

        watch(route, () => {
            if (searchValue.value) searchValue.value = "";
            searchFocused.value = false;
        });

        watch(searchValue, (newValue) => {
            loading.value = newValue && newValue.length >= MIN_QUERY_LENGTH;
        });
        watch(searchValueDebounced, async (newValue) => {
            if (newValue && newValue.length >= MIN_QUERY_LENGTH) {
                await searchQuery(newValue, isLiveRoute.value);
            } else if (newValue?.length === 0) {
                actualQuery.value = "";
                clearResults();
            }
        });

        const $reset = () => {
            searchValue.value = "";
            searchFocused.value = false;
            queries.value = [];
            loading.value = false;
            actualQuery.value = "";
            liveOnlyResults.value = false;
            detailedSearch.value = undefined;
            competitions.value = [];
            events.value = [];
        };

        return {
            searchValue,
            searchValueDebounced,
            searchFocused,
            queries,
            loading,
            actualQuery,
            openedSearchPosition,
            queryMinLength,
            resultEmpty,
            liveOnlyResults,
            detailedSearch,
            competitions,
            events,
            searchQuery,
            clearQueries,
            clearResults,
            saveActualQuery,
            remapToSearchDetail,
            processDetailedSearchEvents,
            processDetailedSearchCompetitions,
            processDetailedSearch,
            $reset,
        };
    },
    {
        persist: {
            pick: ["queries"],
            storage: localStorage,
        },
    },
);

// Enable HMR
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useSearchStore, import.meta.hot));
}
